/* eslint-disable react/jsx-indent, react/jsx-closing-tag-location */
import React, { useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import PanicError from 'Errors/Panic';
import Button1 from 'Components/ComponentLibrary/Components/Buttons/Button1';
import { dataAttrs } from 'Helpers/Testing';
import {
  useMaybeMetaLayoutContext,
} from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';

const styles = theme => ({
  root: {
    backgroundColor: lighten(theme.palette.error.main, 0.8),
    borderRadius: theme.radius.xs,
    padding: theme.spacing(2),
    transition: theme.transitions.create(['height', 'opacity']),
    opacity: 1,
  },
  rootHidden: {
    display: 'none',
    height: 0,
    opacity: 0,
    padding: 0,
  },
  title: {
    color: theme.palette.error.main,
  },
  titleWithErrors: {
    paddingBottom: theme.spacing(1),
  },
  list: {
    margin: 0,
  },
  listItem: {
    color: theme.palette.error.main,
  },
  listItemText: {
    color: theme.palette.error.main,
    textAlign: 'left',
  },
});

function getErrorMessage(err, args) {
  const {
    hideErrorTitle = false,
  } = args;

  if (err.detail && err.title && !hideErrorTitle) {
    return `${err.title}: ${err.detail}`;
  }

  if (err.detail) {
    return err.detail;
  }

  if (err.title) {
    return err.title;
  }

  throw new PanicError('missing-required-error-parameter', 'One of title or detail should be present in an error', {
    err: err.toString(),
  });
}

function ErrorList({
  className,
  classes,
  errors,
  hideErrorTitle,
  title,
  scrollIntoView,
  onErrorLinkClick,
}) {
  const { resource } = useMaybeMetaLayoutContext() ?? {};
  const ref = React.useRef(null);
  // If the frontend generates an error that's just to make a field look red and does not have any real error content, it should be ignored by this error list.
  // For example, fieldsImmediatelyRequired wants to make any required field that's empty look red immediately when the form first renders, but showing an error like "[field name] is required" immediately would be going too far
  const realErrors = errors.filter(err => !err?.fakeError);

  useEffect(() => {
    if (scrollIntoView && realErrors.length) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollIntoView, realErrors]);


  return (
    <div
      ref={ref}
      className={classNames(classes.root, className, {
        [classes.rootHidden]: realErrors.length === 0,
      })}
      {...dataAttrs('error-list', resource)}
    >
      {title && (
        <Typography
          className={classNames(classes.title, {
            [classes.titleWithErrors]: realErrors.length > 0,
          })}
          variant="h5"
        >
          {title}
        </Typography>
      )}
      <ul className={classes.list} test="error-list">
        {realErrors.map((err, i) => (
          <li
            className={classes.listItem}
            key={`err.${i}`} // eslint-disable-line react/no-array-index-key
          >
            {
                  err?.relationships?.errorLocation?.links.related && onErrorLinkClick
                    ? <Button1
                      color={'primary'}
                      variant={'underlined'}
                      onClick={onErrorLinkClick ? () => onErrorLinkClick(err?.relationships?.errorLocation?.links.related) : null}
                      test={'error-link-button'}
                    >
                      <Typography
                        variant="h6"
                        className={classes.listItemText}
                      >
                        {getErrorMessage(err, {
                          hideErrorTitle,
                        })}
                      </Typography>
                      </Button1>
                    : <Typography className={classes.listItemText} variant="h6">
                      {getErrorMessage(err, {
                        hideErrorTitle,
                      })}
                      </Typography>
                }
          </li>
        ))}
      </ul>
    </div>
  );
}

ErrorList.defaultProps = {
  className: '',
  errors: [],
  title: 'Errors occurred while saving:',
  hideErrorTitle: false,
  scrollIntoView: false,
  onErrorLinkClick: null,
};

ErrorList.propTypes = {
  classes: PropTypes.object.isRequired,
  errors: PropTypes.array,
  className: PropTypes.string,
  hideErrorTitle: PropTypes.bool,
  title: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  onErrorLinkClick: PropTypes.func,
};

export default withStyles(styles, { name: 'ErrorList' })(ErrorList);
